import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PersonLinkComponent } from '../shared/person-link.component';
import { UserImageComponent } from '../user-image/user-image.component';
import { MemberMapService } from '../../services/member-map.service';
import { isNew, isOnline, timeAgo } from '../map-pins';
import {
  ChatLink,
  ConversationState,
  MapMember,
  PairConversation,
} from '../../data-transfer-objects/member-map.dto';

export interface ChatLinkDialogData {
  /** Every pair the clicked line stands for, most messages first. */
  links: ChatLink[];
  /** The map's members, so both ends can be named and pictured. */
  members: Map<string, MapMember>;
  /** Joined within this counts as new, as on the map. */
  newWithinMs?: number;
}

/** What the dialog asks the map to do when it closes. */
export interface ChatLinkDialogResult {
  focus: string[];
}

interface Person {
  uid: string;
  member: MapMember | undefined;
  name: string;
  wrote: number;
  started: boolean;
}

const STATE_LABEL: Record<ConversationState, string> = {
  conversation: 'Real conversation',
  replied: 'Replied',
  'opened-no-reply': 'Opened, no reply',
  unopened: 'Not opened yet',
};

/**
 * Who wrote to whom along one chat line on the members map.
 *
 * The same promise as the events board's peer-chats dialog: participants,
 * counts and timestamps only. Nothing anyone wrote is read, here or on the
 * server, and there is no way from here into the messages. A line between two
 * clusters can stand for many pairs, so the dialog lists them and shows one
 * at a time.
 */
@Component({
  selector: 'app-chat-link-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    PersonLinkComponent,
    UserImageComponent,
  ],
  templateUrl: './chat-link-dialog.component.html',
  styleUrls: ['./chat-link-dialog.component.scss'],
})
export class ChatLinkDialogComponent implements OnInit {
  selected = 0;
  loading = true;
  error: string | null = null;
  /** Per pair key, the chats between those two, newest first. */
  detail = new Map<string, PairConversation[]>();
  /** Why a profile could not be opened, from the person link. */
  profileError: string | null = null;
  private peopleCache = new Map<ChatLink, [Person, Person]>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChatLinkDialogData,
    private dialogRef: MatDialogRef<ChatLinkDialogComponent, ChatLinkDialogResult>,
    private mapService: MemberMapService
  ) {}

  ngOnInit(): void {
    const pairs = this.data.links.slice(0, 25).map((l) => [l.a, l.b] as [string, string]);
    this.mapService.getChatPairs(pairs).subscribe({
      next: (res) => {
        res.pairs.forEach((p) => this.detail.set(this.key(p.a, p.b), p.conversations));
        this.loading = false;
      },
      error: (err) => {
        this.error = `Could not load how the chat went: ${err?.message ?? err}`;
        this.loading = false;
      },
    });
  }

  get link(): ChatLink {
    return this.data.links[this.selected];
  }

  /**
   * The one who wrote first on the left.
   *
   * Built once per link and kept: returning fresh objects on every call made
   * the ngFor below rebuild both cards on each change-detection pass, and the
   * pass a mousedown triggers replaced the name being clicked before the click
   * arrived - so the profile never opened.
   */
  people(link: ChatLink): [Person, Person] {
    let pair = this.peopleCache.get(link);
    if (!pair) {
      const first = link.initiator === link.b ? link.b : link.a;
      const second = first === link.a ? link.b : link.a;
      pair = [this.person(link, first), this.person(link, second)];
      this.peopleCache.set(link, pair);
    }
    return pair;
  }

  trackByUid(_: number, p: Person): string {
    return p.uid;
  }

  conversations(link: ChatLink): PairConversation[] | undefined {
    return this.detail.get(this.key(link.a, link.b));
  }

  nameOf(uid: string): string {
    const m = this.data.members.get(uid);
    return m ? `${m.firstName} ${m.lastName}`.trim() || 'Member' : uid.slice(0, 8);
  }

  work(m: MapMember | undefined): string {
    return [m?.role, m?.company].filter((s) => s && s.trim()).join(' · ');
  }

  seen(m: MapMember | undefined): string | null {
    if (!m) return null;
    if (isOnline(m)) return 'Online now';
    const ago = timeAgo(m.lastSeen);
    return ago ? `Seen ${ago}` : null;
  }

  isNew(m: MapMember | undefined): boolean {
    return !!m && isNew(m, Date.now(), this.data.newWithinMs);
  }

  stateLabel(state: ConversationState): string {
    return STATE_LABEL[state];
  }

  ago(iso: string | null): string {
    return timeAgo(iso) ?? '—';
  }

  date(iso: string | null): string {
    return iso
      ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
      : '—';
  }

  select(i: number): void {
    this.selected = i;
  }

  showOnMap(): void {
    this.dialogRef.close({ focus: [this.link.a, this.link.b] });
  }

  close(): void {
    this.dialogRef.close();
  }

  private person(link: ChatLink, uid: string): Person {
    return {
      uid,
      member: this.data.members.get(uid),
      name: this.nameOf(uid),
      wrote: link.perSender?.[uid] ?? 0,
      started: link.initiator === uid,
    };
  }

  private key(a: string, b: string): string {
    return a < b ? `${a}|${b}` : `${b}|${a}`;
  }
}
